import React, { useState } from 'react';
import './App.css';
import { Shield, Users, Clock, MapPin, QrCode, CheckCircle, XCircle, Plus, Trash2, Edit, Search, UserCheck, Activity, LogIn, UserPlus, Lock, Mail, User, Eye, EyeOff, LogOut } from 'lucide-react';

const SSMSystem = () => {
  const [currentPage, setCurrentPage] = useState('login'); // login, register, dashboard
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // User accounts storage
  const [userAccounts, setUserAccounts] = useState([
    {
      id: 1,
      username: 'admin',
      email: 'admin@ssm.com',
      password: 'admin123',
      role: 'admin',
      fullName: 'System Administrator'
    }
  ]);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'guard'
  });

  const [guards, setGuards] = useState([
    {
      id: 'GRD001',
      name: 'John Smith',
      phone: '+91 98765 43210',
      email: 'john.smith@security.com',
      status: 'present',
      location: 'Main Entrance',
      availability: 'available',
      checkInTime: '08:00 AM',
      assignedArea: 'Building A',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John'
    },
    {
      id: 'GRD002',
      name: 'Sarah Johnson',
      phone: '+91 98765 43211',
      email: 'sarah.j@security.com',
      status: 'present',
      location: 'Parking Lot',
      availability: 'on-duty',
      checkInTime: '08:15 AM',
      assignedArea: 'Parking Zone',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah'
    }
  ]);

  const [activities, setActivities] = useState([
    {
      id: 1,
      guardId: 'GRD001',
      guardName: 'John Smith',
      action: 'Arrived',
      location: 'Main Entrance',
      code: '4782',
      timestamp: new Date().toLocaleString(),
      status: 'completed'
    }
  ]);

  const [showAddGuard, setShowAddGuard] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedGuard, setSelectedGuard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const [newGuard, setNewGuard] = useState({
    name: '',
    phone: '',
    email: '',
    id: '',
    assignedArea: ''
  });

  const [statusUpdate, setStatusUpdate] = useState({
    guardName: '',
    guardId: '',
    code: '',
    location: '',
    status: 'arrived'
  });

  // Handle Login
  const handleLogin = (e) => {
    e.preventDefault();
    const user = userAccounts.find(
      u => u.username === loginForm.username && u.password === loginForm.password
    );
    
    if (user) {
      setCurrentUser(user);
      setIsAuthenticated(true);
      setCurrentPage('dashboard');
      setLoginForm({ username: '', password: '' });
    } else {
      alert('Invalid username or password!');
    }
  };

  // Handle Register
  const handleRegister = (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    if (userAccounts.some(u => u.username === registerForm.username)) {
      alert('Username already exists!');
      return;
    }

    if (userAccounts.some(u => u.email === registerForm.email)) {
      alert('Email already exists!');
      return;
    }

    const newUser = {
      id: userAccounts.length + 1,
      username: registerForm.username,
      email: registerForm.email,
      password: registerForm.password,
      role: registerForm.role,
      fullName: registerForm.fullName
    };

    setUserAccounts([...userAccounts, newUser]);
    alert('Account created successfully! Please login.');
    setCurrentPage('login');
    setRegisterForm({
      fullName: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'guard'
    });
  };

  // Handle Logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentPage('login');
  };

  const generateQRCode = () => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setQrCode(code);
    setShowQRScanner(true);
  };

  const handleAttendance = (guardId, status) => {
    setGuards(guards.map(g => 
      g.id === guardId 
        ? { ...g, status, checkInTime: status === 'present' ? new Date().toLocaleTimeString() : g.checkInTime }
        : g
    ));

    const guard = guards.find(g => g.id === guardId);
    addActivity({
      guardId: guard.id,
      guardName: guard.name,
      action: status === 'present' ? 'Checked In' : 'Checked Out',
      location: guard.location,
      timestamp: new Date().toLocaleString(),
      status: 'completed'
    });
  };

  const addGuard = () => {
    if (newGuard.name && newGuard.phone && newGuard.id) {
      const guard = {
        ...newGuard,
        status: 'absent',
        location: '-',
        availability: 'available',
        checkInTime: '-',
        image: `https://api.dicebear.com/7.x/avataaars/svg?seed=${newGuard.name}`
      };
      setGuards([...guards, guard]);
      setNewGuard({ name: '', phone: '', email: '', id: '', assignedArea: '' });
      setShowAddGuard(false);
    }
  };

  const deleteGuard = (id) => {
    setGuards(guards.filter(g => g.id !== id));
  };

  const updateAvailability = (guardId, availability) => {
    setGuards(guards.map(g => 
      g.id === guardId ? { ...g, availability } : g
    ));

    const guard = guards.find(g => g.id === guardId);
    addActivity({
      guardId: guard.id,
      guardName: guard.name,
      action: `Status: ${availability}`,
      location: guard.location,
      timestamp: new Date().toLocaleString(),
      status: 'info'
    });
  };

  const verifyQRCode = () => {
    if (verificationCode === qrCode) {
      setShowQRScanner(false);
      setShowStatusUpdate(true);
      setVerificationCode('');
    } else {
      alert('Invalid code! Please try again.');
    }
  };

  const submitStatusUpdate = () => {
    if (statusUpdate.guardName && statusUpdate.guardId && statusUpdate.code && statusUpdate.location) {
      const guard = guards.find(g => g.id === statusUpdate.guardId);
      
      if (guard) {
        setGuards(guards.map(g => 
          g.id === statusUpdate.guardId 
            ? { ...g, location: statusUpdate.location, status: 'present' }
            : g
        ));
      }

      addActivity({
        guardId: statusUpdate.guardId,
        guardName: statusUpdate.guardName,
        action: statusUpdate.status.charAt(0).toUpperCase() + statusUpdate.status.slice(1),
        location: statusUpdate.location,
        code: statusUpdate.code,
        timestamp: new Date().toLocaleString(),
        status: statusUpdate.status === 'completed' ? 'completed' : 'active'
      });

      setStatusUpdate({ guardName: '', guardId: '', code: '', location: '', status: 'arrived' });
      setShowStatusUpdate(false);
    }
  };

  const addActivity = (activity) => {
    setActivities([{ ...activity, id: Date.now() }, ...activities]);
  };

  const deleteActivity = (id) => {
    setActivities(activities.filter(a => a.id !== id));
  };

  const filteredGuards = guards.filter(g => {
    const matchesSearch = g.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          g.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || g.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: guards.length,
    present: guards.filter(g => g.status === 'present').length,
    absent: guards.filter(g => g.status === 'absent').length,
    available: guards.filter(g => g.availability === 'available').length
  };

  // Login Page
  if (currentPage === 'login' && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-blue-500/30 shadow-2xl max-w-md w-full overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl mb-4 shadow-lg shadow-blue-500/50">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
              <p className="text-gray-400">Security Service Management System</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your username"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3 rounded-lg font-semibold shadow-lg shadow-blue-500/50 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Sign In
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Don't have an account?{' '}
                <button
                  onClick={() => setCurrentPage('register')}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                >
                  Create Account
                </button>
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-xs text-gray-500 text-center">
                Demo: admin / admin123
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Register Page
  if (currentPage === 'register' && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-purple-500/30 shadow-2xl max-w-md w-full overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500"></div>
          
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl mb-4 shadow-lg shadow-purple-500/50">
                <UserPlus className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
              <p className="text-gray-400">Join our security management team</p>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={registerForm.fullName}
                    onChange={(e) => setRegisterForm({...registerForm, fullName: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Choose a username"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                <select
                  value={registerForm.role}
                  onChange={(e) => setRegisterForm({...registerForm, role: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="guard">Security Guard</option>
                  <option value="admin">Administrator</option>
                  <option value="supervisor">Supervisor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                    className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                    className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg shadow-purple-500/50 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Create Account
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Already have an account?{' '}
                <button
                  onClick={() => setCurrentPage('login')}
                  className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard (existing code)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <header className="bg-slate-800/50 backdrop-blur-lg border-b border-blue-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 rounded-xl shadow-lg shadow-blue-500/50">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Security Service Management</h1>
                <p className="text-blue-300 text-sm">Welcome, {currentUser?.fullName || 'User'}</p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <div className="text-right mr-4">
                <p className="text-white font-semibold">{currentUser?.fullName}</p>
                <p className="text-blue-300 text-sm capitalize">{currentUser?.role}</p>
              </div>
              <button
                onClick={generateQRCode}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-all"
              >
                <QrCode className="w-5 h-5" />
                Scan QR
              </button>
              <button
                onClick={() => setShowAddGuard(true)}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Guard
              </button>
              <button
                onClick={handleLogout}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-all"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Guards</p>
                <h3 className="text-3xl font-bold text-white mt-1">{stats.total}</h3>
              </div>
              <Users className="w-12 h-12 text-blue-200 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Present Today</p>
                <h3 className="text-3xl font-bold text-white mt-1">{stats.present}</h3>
              </div>
              <CheckCircle className="w-12 h-12 text-green-200 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Absent Today</p>
                <h3 className="text-3xl font-bold text-white mt-1">{stats.absent}</h3>
              </div>
              <XCircle className="w-12 h-12 text-red-200 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Available</p>
                <h3 className="text-3xl font-bold text-white mt-1">{stats.available}</h3>
              </div>
              <Activity className="w-12 h-12 text-purple-200 opacity-80" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 mb-8 border border-blue-500/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'present', 'absent'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    filterStatus === status
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl border border-blue-500/30 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <UserCheck className="w-6 h-6" />
                  Guard Management
                </h2>
              </div>
              
              <div className="divide-y divide-slate-700">
                {filteredGuards.map(guard => (
                  <div key={guard.id} className="p-6 hover:bg-slate-700/30 transition-all">
                    <div className="flex items-start gap-4">
                      <img 
                        src={guard.image} 
                        alt={guard.name}
                        className="w-16 h-16 rounded-full border-4 border-blue-500/30"
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-bold text-white">{guard.name}</h3>
                            <p className="text-blue-300 text-sm">ID: {guard.id}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedGuard(guard)}
                              className="p-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-all"
                              title="Edit Guard"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteGuard(guard.id)}
                              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
                              title="Delete Guard"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                          <div className="flex items-center gap-2 text-gray-300">
                            <Clock className="w-4 h-4 text-blue-400" />
                            {guard.checkInTime}
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <MapPin className="w-4 h-4 text-green-400" />
                            {guard.location}
                          </div>
                          <div className="text-gray-300">
                            📞 {guard.phone}
                          </div>
                          <div className="text-gray-300">
                            🏢 {guard.assignedArea}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            guard.status === 'present' 
                              ? 'bg-green-500/20 text-green-300 border border-green-500/50' 
                              : 'bg-red-500/20 text-red-300 border border-red-500/50'
                          }`}>
                            {guard.status}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            guard.availability === 'available' 
                              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50' 
                              : guard.availability === 'on-duty'
                              ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                              : 'bg-gray-500/20 text-gray-300 border border-gray-500/50'
                          }`}>
                            {guard.availability}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleAttendance(guard.id, guard.status === 'present' ? 'absent' : 'present')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              guard.status === 'present'
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {guard.status === 'present' ? 'Mark Absent' : 'Mark Present'}
                          </button>
                          
                          <select
                            value={guard.availability}
                            onChange={(e) => updateAvailability(guard.id, e.target.value)}
                            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="available">Available</option>
                            <option value="on-duty">On Duty</option>
                            <option value="not-available">Not Available</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl border border-blue-500/30 overflow-hidden sticky top-24">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Activity className="w-6 h-6" />
                  Recent Activity
                </h2>
              </div>
              
              <div className="max-h-[600px] overflow-y-auto">
                {activities.map(activity => (
                  <div key={activity.id} className="p-4 border-b border-slate-700 hover:bg-slate-700/30 transition-all">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-white font-semibold">{activity.guardName}</p>
                        <p className="text-sm text-blue-300">ID: {activity.guardId}</p>
                      </div>
                      <button
                        onClick={() => deleteActivity(activity.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-gray-300 text-sm mb-2">
                      <span className="font-semibold text-cyan-400">{activity.action}</span>
                      {activity.code && <span className="ml-2">Code: {activity.code}</span>}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <MapPin className="w-3 h-3" />
                      {activity.location}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddGuard && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-blue-500/30 shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Add New Guard</h3>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                value={newGuard.name}
                onChange={(e) => setNewGuard({...newGuard, name: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Guard ID"
                value={newGuard.id}
                onChange={(e) => setNewGuard({...newGuard, id: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={newGuard.phone}
                onChange={(e) => setNewGuard({...newGuard, phone: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Email"
                value={newGuard.email}
                onChange={(e) => setNewGuard({...newGuard, email: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Assigned Area"
                value={newGuard.assignedArea}
                onChange={(e) => setNewGuard({...newGuard, assignedArea: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-3 pt-4">
                <button
                  onClick={addGuard}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Add Guard
                </button>
                <button
                  onClick={() => setShowAddGuard(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQRScanner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-purple-500/30 shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">QR Code Verification</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-white p-8 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <QrCode className="w-24 h-24 mx-auto text-slate-800 mb-4" />
                  <p className="text-4xl font-bold text-slate-800 tracking-wider">{qrCode}</p>
                  <p className="text-sm text-gray-600 mt-2">Scan this code with Google Lens</p>
                </div>
              </div>
              <input
                type="text"
                placeholder="Enter code from QR scan"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center text-2xl font-bold placeholder-gray-400 focus:ring-2 focus:ring-purple-500"
              />
              <div className="flex gap-3 pt-4">
                <button
                  onClick={verifyQRCode}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Verify
                </button>
                <button
                  onClick={() => {
                    setShowQRScanner(false);
                    setVerificationCode('');
                  }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStatusUpdate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-cyan-500/30 shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Update Status</h3>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Guard Name"
                value={statusUpdate.guardName}
                onChange={(e) => setStatusUpdate({...statusUpdate, guardName: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500"
              />
              <input
                type="text"
                placeholder="Guard ID"
                value={statusUpdate.guardId}
                onChange={(e) => setStatusUpdate({...statusUpdate, guardId: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500"
              />
              <input
                type="text"
                placeholder="Code Number"
                value={statusUpdate.code}
                onChange={(e) => setStatusUpdate({...statusUpdate, code: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500"
              />
              <input
                type="text"
                placeholder="Location/Place"
                value={statusUpdate.location}
                onChange={(e) => setStatusUpdate({...statusUpdate, location: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500"
              />
              <select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="arrived">Arrived</option>
                <option value="on-duty">On Duty</option>
                <option value="completed">Completed</option>
              </select>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={submitStatusUpdate}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Submit
                </button>
                <button
                  onClick={() => setShowStatusUpdate(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedGuard && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-orange-500/30 shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Edit Guard Details</h3>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                value={selectedGuard.name}
                onChange={(e) => setSelectedGuard({...selectedGuard, name: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={selectedGuard.phone}
                onChange={(e) => setSelectedGuard({...selectedGuard, phone: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="email"
                placeholder="Email"
                value={selectedGuard.email}
                onChange={(e) => setSelectedGuard({...selectedGuard, email: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="text"
                placeholder="Assigned Area"
                value={selectedGuard.assignedArea}
                onChange={(e) => setSelectedGuard({...selectedGuard, assignedArea: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="text"
                placeholder="Location"
                value={selectedGuard.location}
                onChange={(e) => setSelectedGuard({...selectedGuard, location: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500"
              />
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setGuards(guards.map(g => g.id === selectedGuard.id ? selectedGuard : g));
                    setSelectedGuard(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setSelectedGuard(null)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-8 right-8 flex flex-col gap-3">
        <button
          onClick={generateQRCode}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
          title="Quick QR Scan"
        >
          <QrCode className="w-6 h-6" />
        </button>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
          title="Scroll to Top"
        >
          ↑
        </button>
      </div>
    </div>
  );
};

export default SSMSystem;